import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "api";
import { CreditScoreGauge } from "./CreditScoreGauge";
import { CreditScore } from "types/credit-score";
import { Show } from "shared/components";
import { Button, ButtonVariant } from "shared/components/Button";
import { formatDate } from "date-fns";
import {
  CreditScoreTimeLine,
  CreditScoreTimeLineItem
} from "common/CreditScoreTimeline";
import { Loader } from "shared/components/Loader";

const INITIAL_DISPLAY_COUNT = 9;

export const CreditMonitoringContent = () => {
  const [showAll, setShowAll] = useState(false);

  const {
    data: creditScores,
    isError,
    isPending
  } = useQuery({
    queryKey: ["CREDIT_SCORES"],
    queryFn: () => api.getCreditScores().then((res) => res.data)
  });

  const latestScore = creditScores?.[0];
  const hasValidScore = Boolean(
    latestScore?.score && latestScore?.score >= 300
  );
  const hasMoreScores = (creditScores?.length ?? 0) > INITIAL_DISPLAY_COUNT;
  const displayedScores = showAll
    ? creditScores
    : creditScores?.slice(0, INITIAL_DISPLAY_COUNT);

  const scoreMessage = useMemo(() => {
    if (isError) return "Error loading credit scores";
    if (!hasValidScore) return "Credit score is not available at this time";
    return "Score is an Equifax VantageScore 4.0";
  }, [isError, hasValidScore]);

  if (isPending)
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );

  return (
    <div className="max-w-[440px] mx-auto">
      <div className="py-6">
        <CreditScoreGauge score={latestScore?.score} />
      </div>

      <p className="text-center text-md font-normal text-content-600 mb-6 border-b border-alpha-40 pb-6">
        {scoreMessage}
      </p>

      <Show if={creditScores && creditScores.length > 0 && !isError}>
        <div>
          <h3 className="text-sm font-medium text-content-700 mb-4">
            Credit score timeline
          </h3>
          <CreditScoreTimeLine noBorder wrapperClassName="ml-0 pl-0">
            {displayedScores?.map((score: CreditScore, index: number) => (
              <CreditScoreTimeLineItem
                key={score.id}
                isFirst={index === 0}
                isLast={index === displayedScores.length - 1}
                wrapperClassName="h-12 items-center"
                childrenClassName="flex-1 ml-3"
                hideCheck
              >
                <div className="flex justify-between items-center w-full gap-4">
                  <p className="text-sm font-medium text-content-700 shrink-0">
                    {formatDate(score.reported_at, "MMM d, yyyy")}
                  </p>
                  <div className="border-t border-dashed border-alpha-100 flex-1" />
                  <span className="px-3 py-1 text-sm font-medium text-primary-500 bg-primary-20 rounded-md shrink-0">
                    {score.score}
                  </span>
                </div>
              </CreditScoreTimeLineItem>
            ))}
          </CreditScoreTimeLine>
          <Show if={hasMoreScores && !showAll}>
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full my-4"
              onClick={() => setShowAll(true)}
            >
              Show more
            </Button>
          </Show>
          <Show if={showAll}>
            <div className="border-b border-alpha-40 my-4" />
          </Show>
        </div>
      </Show>
    </div>
  );
};
